import { useCallback, useEffect, useState } from 'react';
import noop from '../noop';

// eslint-disable-next-line @typescript-eslint/no-explicit-any

/**
 * Expose an interface to implement a controlled component
 * @param defaultValue Default value for the input using the hook
 * @param value Current value
 * @param onChange Callback to be fired on input change
 * @returns a tuple containing the final value and a callback for change event which runs `onChange`
 * @example
 * const ControlledInput = React.forwardRef(
 *   (
 *     {
 *       prop,
 *       defaultProp = false,
 *       onChange,
 *       ...props
 *     },
 *     ref
 *   ) => {
 *     const [value, handleOnChange] = useControlled(
 *       defaultProp,
 *       prop,
 *       onChange
 *     );
 *
 *     return (
 *       <input
 *         ref={ref}
 *         name={name}
 *         value={value}
 *         onChange={onChange}
 *       />
 *     );
 *   }
 * )
 * @see Button Checkbox CommentField Menu RadioButton Slider Table Tag Tile Toggle
 */
export default function useControlled(defaultValue, value) {
  let onChange = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : noop;
  const [innerValue, setInnerValue] = useState(defaultValue);

  // Sync internal state with controlled value when `value` changes
  useEffect(() => {
    if (value !== undefined) {
      setInnerValue(value);
    }
  }, [value]);
  const handleChange = useCallback(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function (v) {
    if (value === undefined) {
      setInnerValue(v);
    }
    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }
    onChange(v, ...args);
  }, [onChange, value]);
  return [value !== undefined ? value : innerValue, handleChange];
}
//# sourceMappingURL=useControlled.js.map